<script lang="ts">
	// IMPORTS
	import Block from '$components/Block/Block.svelte';
	import { gsap } from '$utils/gsap.svelte.js';
	import { ScrollTrigger } from '$utils/gsap.svelte.js';
	import NarrationControl from './NarrationControl.svelte';
	import { parseTimestampSimple } from '$lib/captions';
	import { mutedStore } from '$stores/mutedStore';
	import { autoScrollStore } from '$stores/autoScrollStore';
	import { assets } from '$app/paths';

	// TYPES
	type AudioId = string;

	type AudioTextData = Array<{
		text: string;
		start: string;
		end: string;
	}>;

	type FormattedData = Array<{
		text: string;
		start: number;
		end: number;
	}>;

	// PROPS
	export let id: AudioId;
	export let data: AudioTextData;

	// VARIABLES
	let active = false;
	let formattedData: FormattedData = [];
	let time = 0;
	let audioEl: HTMLAudioElement;
	let muted = true;
	let activeIndex = -1;
	let activeElement: Array<HTMLElement> = [];
	let lastScrollIndex = -1;
	let autoscroll = false;
	let scrollY: number;
	let ended = false;
	let st: ScrollTrigger;

	// LOGIC
	function formatData(data: AudioTextData) {
		return data.map((item) => {
			const { start, end } = item;
			const startTime = parseTimestampSimple(start);
			const endTime = parseTimestampSimple(end);

			return {
				...item,
				start: startTime,
				end: endTime,
			};
		});
	}

	function checkAutoplay() {
		if (!muted && time === 0) {
			audioEl.play();
		}
	}

	function setupAudioText(el: HTMLElement) {
		const articleEl = document.querySelector('article');
		const tl = gsap.timeline();

		formattedData = formatData(data);

		st = ScrollTrigger.create({
			trigger: el,
			start: 'top 20%',
			end: 'bottom 40%',
			onEnter: () => {
				tl.to(articleEl, { backgroundColor: '#202223' });
				active = true;
				checkAutoplay();
			},
			onEnterBack: () => {
				tl.to(articleEl, { backgroundColor: '#202223' });
				active = true;
				checkAutoplay();
			},
			onLeave: () => {
				tl.to(articleEl, { backgroundColor: '#f6e8da' });
				active = false;
				audioEl.pause();
			},
			onLeaveBack: () => {
				tl.to(articleEl, { backgroundColor: '#f6e8da' });
				active = false;
				audioEl.pause();
			},
		});
	}

	function handleAudioInfo(
		event: CustomEvent<{
			time: number;
			paused: boolean;
			audioEl: HTMLAudioElement;
			ended: boolean;
		}>
	) {
		({ time, audioEl, audioEl, ended } = event.detail);
	}

	function handleAudioEnded() {
		const bodyTextElements = document.querySelectorAll('.BodyText');
		let nextElement: HTMLElement | null = null;

		bodyTextElements.forEach((el) => {
			if (el instanceof HTMLElement) {
				const rect = el.getBoundingClientRect();
				if (rect.top > 0 && nextElement === null) {
					nextElement = el;
				}
			}
		});

		if (nextElement) {
			gsap.to(window, {
				scrollTo: { y: nextElement.offsetTop },
				duration: 1,
				ease: 'Power3.easeInOut',
			});
		}
	}

	// REACITVITY
	$: {
		const isLastElement = (index: number, array: FormattedData) =>
			index === array.length - 1;

		muted = $mutedStore.muted;
		autoscroll = $autoScrollStore.enabled;

		if (formattedData.length > 0) {
			const newIndex = formattedData.findIndex((item, index) => {
				const endTime = isLastElement(index, formattedData)
					? audioEl.duration
					: item.end;
				return time >= item.start && time <= endTime;
			});

			if (newIndex !== -1) {
				activeIndex = newIndex;
			}
		}

		if (autoscroll && activeIndex !== -1 && activeIndex !== lastScrollIndex) {
			const el = activeElement[activeIndex];

			if (el) {
				const minY = st.start;
				const targetY =
					el.offsetTop - window.innerHeight / 2 + el.clientHeight / 2;
				if (targetY >= minY) {
					gsap.to(window, {
						scrollTo: {
							y: targetY,
						},
						duration: 0.5,
						ease: 'Power3.easeInOut',
					});
					lastScrollIndex = activeIndex; // Update lastScrollIndex
				}
			}
		}

		if (ended && autoscroll) {
			console.log('ended', scrollY);
			ended = false;
			handleAudioEnded();
		}
	}
</script>

<svelte:window bind:scrollY />

<Block id="{`block-${id}`}" width="full-width">
	<div class="AudioText" use:setupAudioText {id} class:active class:muted>
		<div class="container">
			{#each data as { text, start, end }, i}
				{#if i === 0}
					<div class="header">
						<div class="header-art" {id}>
							<img src="{`${assets}/images/${id}.svg`}" alt="{id}" />
						</div>
						<h2
							class:active="{activeIndex === i}"
							class:previously-active="{i < activeIndex}"
							class:inactive="{i > activeIndex}"
							data-start="{start}"
							data-end="{end}"
						>
							{text}
						</h2>
						<div class="separator"></div>
					</div>
				{:else}
					<div class="text">
						<p
							bind:this="{activeElement[i]}"
							data-index="{i}"
							class:active="{activeIndex === i}"
							class:previously-active="{i < activeIndex}"
							class:inactive="{i > activeIndex}"
						>
							{text}
						</p>
					</div>
				{/if}
			{/each}
		</div>
	</div>
	<NarrationControl
		{id}
		{active}
		{muted}
		on:audioInfo="{handleAudioInfo}"
		{ended}
	/>
</Block>

<style lang="scss">
	@use '$styles/mq' as *;
	@use '$styles/colors' as *;

	#september-13 {
		margin-bottom: 0;
		padding-bottom: 0;
	}

	.AudioText {
		display: flex;
		justify-content: center;
		align-items: center;
		background: var(--theme-primary);
		padding-top: var(--theme-section-spacing);
		padding-bottom: var(--theme-section-spacing);
		min-height: 100lvh;
		color: var(--theme-color-background);
		font-family: var(--brand-font-copy);
	}

	.container {
		margin: 0 auto;
		padding: 40px 0;
		padding-right: 12px;
		padding-left: 12px;
		max-width: 500px;
	}

	.header {
		position: relative;
	}

	.header-art#july-21 {
		margin: 0 auto;
		max-width: 250px;
	}

	// .header-art not with id july-21
	.header-art:not(#july-21) {
		position: absolute;
		top: calc((var(--theme-section-spacing) * -1) - 271px);
		left: 0;
		z-index: 0;
		width: 100%;
		img {
			display: block;
			margin: 0 auto;
			width: 100%;
			max-width: 500px;
			height: 400px;
		}
	}

	img {
		display: block;
		width: 100%;
		height: auto;
	}

	.header h2 {
		position: relative;
		z-index: 100;
		margin: 32px auto;
		font-weight: 700;
		font-size: 1.5rem;
		font-family: var(--brand-font-ui-cn);
		text-align: center;
		text-transform: uppercase;
	}

	.separator {
		margin: 0 auto;
		margin-bottom: 32px;
		background: var(--theme-accent);
		width: 60px;
		height: 5px;
	}

	.AudioText.muted .text p {
		opacity: 1;

		&.previously-active {
			opacity: 1;
		}

		&.active {
			opacity: 1;
		}
	}

	.text p {
		opacity: 0.1;
		transition: all 0.5s ease-in-out;
		margin-bottom: 32px;
		font-size: 1.2rem;
		line-height: 1.8;

		&.previously-active {
			opacity: 0.5;
		}

		&.active {
			opacity: 1;
		}

		&[data-index='1'] {
			font-style: italic;
			font-weight: 700;
			font-size: 2rem;
			line-height: 1.3;
		}
	}
</style>
